import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { RowAction } from "_shared/_views/tables/row-actions";
import { touchTargetSx } from "_shared/_views/touch-target";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useLazyGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import GrantRow, { GrantRowTable } from "permissions/_views/grant-row/grant-row";
import { buildGrantRow, grantPeopleIds } from "permissions/_views/grant-row/grant-row-model";
import FieldAccessDialog from "permissions/_views/people/field-access-dialog";
import { type GrantDetailsSelection, detailsSignature } from "permissions/_views/people/person-grant-details";
import {
  NAME_SEARCH_LIMIT,
  type PersonRecordRow,
  disclosureLabel,
  fieldCeilings,
  fieldCounts,
  isFieldKind,
} from "permissions/_views/people/person-record-grants-model";
import type { PersonGrantChanges } from "permissions/_views/people/use-person-grant-changes";
import usePersonRecordGrants, { type RecordListMode } from "permissions/_views/people/use-person-record-grants";
import type { RecordSearchScope } from "permissions/_views/people/use-record-name-search";
import { type PersonNames, personNameState, removerNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import PermissionTargetCell from "permissions/_views/targets/permission-target-cell";
import { type NameState, targetStateText } from "permissions/_views/targets/permission-target-state";
import type { FieldAccessValues } from "permissions/_views/people/field-access-dialog";
import {
  REMOVED_JUST_NOW,
  accessibleNames,
  fieldSummary,
  kindLabel,
  noSearchMatch,
  recordCount,
  removedRecordCount,
} from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import { type ReactNode, useEffect, useId, useMemo, useState } from "react";

/**
 * One per-record kind on the person page: a count row, "One proposal · 12 proposal grants · Show", and,
 * once shown, the person's grants of that kind in place (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "The person page").
 *
 * The list extends the page; it has no scroll of its own. It is paged on the server, and which kind is
 * open, its page and its search are the page's query string, so Back closes it and a link opens it
 * again. Nothing about a person's 587 document grants is read until Show is pressed.
 *
 * Each row is one record: the strongest grant on it, with a disclosure to the others when more than
 * one opens it. A row whose record cannot be named says why, in the kind's noun.
 */

const SEARCH_DELAY_MS = 300;

/** Enough for every live grant one person holds on one record: a few direct and derived ones at most. */
const GRANTS_ON_ONE_RECORD = 100;

/** The person's live grants on one record, matched by containment on the record's id. */
function liveGrantsOnRecord(userId: Guid, kind: PermissionKind, targetId: string): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: GRANTS_ON_ONE_RECORD }),
    filterParameters: new PermissionFilterParameters({
      granteeUserIds: [userId],
      resource: { resourceType: kind.resourceType, [kind.targetIdProperty!]: targetId },
      showDisabled: false,
    }),
  });
}

export type PersonRecordListProps = Readonly<{
  kind: PermissionKind;
  /** The server's count of the person's grants of this kind: live ones, or removed ones in removed mode. */
  count: number;
  /** Live grants, or under the person page's Removed filter the removed ones. Never both. */
  mode?: RecordListMode;
  kinds: readonly PermissionKind[];
  person: Readonly<{ userId: Guid; name: string }>;
  viewerUserId: Guid;
  mayAdministerPlatform: boolean;
  readOnly: boolean;
  /**
   * Which records the name search can find: every record of the organization for a viewer who holds
   * Member Permissions, or only those they may open (#6795 review). Every record by default.
   */
  searchScope?: RecordSearchScope;
  changes: PersonGrantChanges;
  open: boolean;
  /** Zero-based. */
  pageIndex: number;
  query: string;
  /**
   * The one record the list is narrowed to, after a change from Add access to a grant on it: that
   * grant keeps its created date, so it may sit on any page of the list (#6837).
   */
  targetId?: string;
  onToggle: () => void;
  onPage: (pageIndex: number) => void;
  onQuery: (query: string) => void;
  /** Lifts the narrowing to one record, back to every record on the first page. */
  onShowAll?: () => void;
  /**
   * The xl details pane's "Show details", on every row. Without it the rows offer none. The list calls
   * it again when a fresher copy of the selected grant would change what the pane shows.
   */
  onShowDetails?: (details: GrantDetailsSelection) => void;
  /** What the details pane shows now, so the list can keep its own selected grant current. */
  selectedDetails?: GrantDetailsSelection;
}>;

/** The text a record stands for in control names: its name, or the state that stands in for it. */
function targetLabel(state: NameState | undefined, noun: string): string | undefined {
  if (!state || state.state === "loading") return undefined;
  return state.state === "found" ? state.name : targetStateText(state, noun);
}

function useGrantorNames(grants: readonly Permission[]): PersonNames {
  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const ids = useMemo(() => new Set(grants.flatMap(grantPeopleIds)), [grants]);
  return usePersonNames(ids);
}

function RecordRows(props: PersonRecordListProps & Readonly<{ listId: string }>) {
  const { kind, kinds, person, viewerUserId, mayAdministerPlatform, readOnly, changes, pageIndex, query, onPage, onQuery, onShowDetails } =
    props;
  const noun = kind.targetNoun ?? kind.name;
  const kindName = kindLabel(kind);
  const mode = props.mode ?? "live";
  const narrowed = props.targetId !== undefined;
  const records = usePersonRecordGrants(person.userId, kind, pageIndex, query, mode, props.searchScope, props.targetId);
  const [readGrants] = useLazyGetPermissionsQuery();
  const [text, setText] = useState(query);
  const [shownQuery, setShownQuery] = useState(query);
  // A search set from outside — cleared after a grant, or by Back — replaces what was typed, or the
  // typing would write the old search back once it pauses.
  if (query !== shownQuery) {
    setShownQuery(query);
    if (query.trim() !== text.trim()) setText(query);
  }
  const [disclosed, setDisclosed] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<Readonly<{ row: PersonRecordRow; permission: Permission }>>();

  // Typing is written to the query string once it pauses, so each keystroke is not a history entry.
  useEffect(() => {
    if (text.trim() === query.trim()) return;
    const timer = setTimeout(() => onQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, query, onQuery]);

  const allGrants = useMemo(() => records.rows.flatMap((row) => row.grants), [records.rows]);
  const grantorNames = useGrantorNames(allGrants);
  const fields = isFieldKind(kind);

  // Every live grant on the record, not only the ones on this page: the list pages grants, so a
  // record's other grants can sit on another page. Read by containment on the record's id; if that
  // read fails, the grants this page holds are removed and the rest stay listed.
  const removeAll = async (row: PersonRecordRow) => {
    let grants = row.grants.filter((grant) => !grant.isDisabled);
    if (kind.targetIdProperty && row.targetId) {
      const all = await readGrants({ query: liveGrantsOnRecord(person.userId, kind, row.targetId) })
        .unwrap()
        .then((page) => Permission.fromJSONArray(page.data))
        .catch(() => undefined);
      if (all) grants = all.filter((grant) => !grant.isDisabled);
    }
    for (const grant of grants) await changes.remove(grant, kind);
  };

  const detailsOf = (row: PersonRecordRow, permission: Permission, state: NameState | undefined): GrantDetailsSelection => {
    const built = buildGrantRow({
      kind,
      kinds,
      permission,
      viewerUserId,
      mayAdministerPlatform,
      grantor: personNameState(grantorNames, permission.grantorUserId.value),
      remover: removerNameState(grantorNames, permission),
    });
    const removedHere = changes.removedJustNow.has(permission.id?.value ?? row.key) && permission.isDisabled;
    return {
      row: removedHere ? { ...built, removedText: REMOVED_JUST_NOW } : built,
      permission,
      target: { label: targetLabel(state, noun), id: row.targetId, noun },
    };
  };

  // The pane keeps showing the grant chosen here while this list redraws it — after a change, or once
  // its record or grantor is named — so it does not describe the grant as it was when chosen. It
  // replaces the selection only when the pane would change, so the redraw it causes ends here.
  const selectedId = props.selectedDetails?.permission?.id?.value;
  useEffect(() => {
    if (!selectedId || !onShowDetails) return;
    const row = records.rows.find((candidate) => candidate.grants.some((grant) => grant.id?.value === selectedId));
    const permission = row?.grants.find((grant) => grant.id?.value === selectedId);
    if (!row || !permission) return;
    const fresh = detailsOf(row, permission, row.targetId ? records.targets.get(row.targetId) : undefined);
    if (detailsSignature(fresh) !== detailsSignature(props.selectedDetails)) onShowDetails(fresh);
  });

  const renderGrant = (row: PersonRecordRow, permission: Permission, state: NameState | undefined, isPrimary: boolean): ReactNode => {
    const key = permission.id?.value ?? row.key;
    const label = targetLabel(state, noun);
    const model = detailsOf(row, permission, state).row;
    const derived = model.lock?.reason === "derived";
    const disclosure = isPrimary ? disclosureLabel(row, kinds) : undefined;
    const isOpen = disclosed.has(row.key);
    const subRowsId = `${props.listId}-${row.key}-grants`;
    const actions: RowAction[] = onShowDetails
      ? [
          {
            key: "details",
            label: "Show details",
            ariaLabel: `Show details of ${kindName} for ${person.name}${label ? `, ${label}` : ""}`,
            icon: <InfoOutlinedIcon />,
            onClick: () => onShowDetails(detailsOf(row, permission, state)),
          },
        ]
      : [];

    const counts = fields ? fieldCounts(permission) : undefined;
    const levelCell = counts ? (
      <Chip
        size="small"
        variant="outlined"
        clickable
        label={fieldSummary(counts.granted, counts.total, counts.writable)}
        aria-label={`${accessibleNames.fields(kindName, person.name, label)}: ${fieldSummary(counts.granted, counts.total, counts.writable)}`}
        aria-haspopup="dialog"
        sx={[touchTargetSx(), derived ? { borderStyle: "dashed" } : model.state === "removed" ? { textDecoration: "line-through" } : {}]}
        onClick={() => setEditing({ row, permission })}
      />
    ) : undefined;

    const target = isPrimary ? (
      <Box sx={{ display: "grid", gap: 0.5, justifyItems: "start" }}>
        <PermissionTargetCell
          state={state ?? { state: "loading" }}
          noun={noun}
          targetId={row.targetId}
          kindName={kindName}
          personName={person.name}
          onRetry={records.retryTargets}
          onRemove={readOnly || row.grants.every((grant) => grant.isDisabled) ? undefined : () => void removeAll(row)}
          pending={row.grants.some((grant) => grant.id && changes.pending.has(grant.id.value))}
        />
        {disclosure && (
          <Button
            size="small"
            color="inherit"
            aria-expanded={isOpen}
            aria-controls={subRowsId}
            aria-label={`${accessibleNames.grants(kindName, person.name, label)}: ${disclosure}`}
            onClick={() =>
              setDisclosed((current) => {
                const next = new Set(current);
                if (next.has(row.key)) next.delete(row.key);
                else next.add(row.key);
                return next;
              })
            }
            startIcon={<ChevronRightIcon sx={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />}
            sx={[{ textTransform: "none", color: "text.secondary", p: 0, minWidth: 0 }, touchTargetSx()]}
          >
            {disclosure}
          </Button>
        )}
      </Box>
    ) : (
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    );

    return (
      <GrantRow
        key={key}
        row={model}
        subject="person"
        personName={person.name}
        header={target}
        targetLabel={label}
        levelCell={levelCell}
        pending={changes.pending.has(key)}
        alert={changes.alerts.get(key)}
        highlight={permission.id ? changes.highlighted.has(permission.id.value) : false}
        onSwitch={readOnly ? undefined : (enabled) => void (enabled ? changes.grantAgain(permission, kind) : changes.remove(permission, kind))}
        onChangeLevel={readOnly ? undefined : (level) => void changes.changeLevel(permission, kind, level)}
        onRetryGrantor={grantorNames.retry}
        actions={actions}
      />
    );
  };

  let body: ReactNode;
  if (records.error) {
    body = (
      <Alert
        severity="error"
        sx={{ m: 1 }}
        action={
          <Button color="inherit" size="small" onClick={records.retry}>
            Retry
          </Button>
        }
      >
        {`Unable to load ${person.name}'s ${noun} grants. ${records.error}`}
      </Alert>
    );
  } else if (records.isLoading) {
    body = (
      <Box aria-busy="true" aria-label={`Loading ${person.name}'s ${noun} grants`} sx={{ display: "grid", gap: 1, p: 2 }}>
        <Skeleton variant="rectangular" height={44} />
        <Skeleton variant="rectangular" height={44} />
        <Skeleton variant="rectangular" height={44} />
      </Box>
    );
  } else if (records.rows.length === 0) {
    body = (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {query
          ? noSearchMatch(noun, query, NAME_SEARCH_LIMIT, props.searchScope === "readable")
          : mode === "removed"
            ? `${person.name} has no removed ${noun} grants.`
            : `${person.name} holds no ${noun} grants.`}
      </Typography>
    );
  } else {
    body = (
      <GrantRowTable label={`${kindName}: ${person.name}'s grants`}>
        {records.rows.flatMap((row) => {
          const state = row.targetId ? records.targets.get(row.targetId) : undefined;
          const rows = [renderGrant(row, row.primary, state, true)];
          if (disclosed.has(row.key)) {
            rows.push(
              <Box key={`${row.key}-grants`} id={`${props.listId}-${row.key}-grants`} role="rowgroup" sx={{ display: "contents", "& [role='row']": { pl: 5 } }}>
                {row.grants.slice(1).map((grant) => renderGrant(row, grant, state, false))}
              </Box>,
            );
          }
          return rows;
        })}
      </GrantRowTable>
    );
  }

  const editingDerived = editing ? (editing.permission.parentPermissions?.length ?? 0) > 0 : false;
  const editingState = editing?.row.targetId ? records.targets.get(editing.row.targetId) : undefined;

  return (
    <Box id={props.listId}>
      {narrowed && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1, px: 2, pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 12rem" }}>
            {`Showing only the ${noun} you just changed.`}
          </Typography>
          {props.onShowAll && (
            <Button size="small" onClick={props.onShowAll} sx={touchTargetSx()}>
              {`Show all ${noun} grants`}
            </Button>
          )}
        </Box>
      )}
      {!narrowed && (records.canSearch || records.searchError) && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          {records.canSearch && (
            <TextField
              size="small"
              fullWidth
              label={`Find a ${noun} by name`}
              value={text}
              onChange={(event) => setText(event.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {records.searchError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {`The ${noun} search failed. ${records.searchError}`}
            </Alert>
          )}
        </Box>
      )}
      {body}
      {records.pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 1.5 }}>
          <Pagination
            sx={touchTargetSx("& .MuiPaginationItem-root")}
            count={records.pageCount}
            page={pageIndex + 1}
            onChange={(_, page) => onPage(page - 1)}
            getItemAriaLabel={(type, page) =>
              type === "page" ? `${kindName}, page ${page}` : `${type === "next" ? "Next" : type === "previous" ? "Previous" : type} page of ${noun} grants`
            }
          />
        </Box>
      )}
      {editing && (
        <FieldAccessDialog
          open
          kindName={kindName}
          personName={person.name}
          targetName={targetLabel(editingState, noun)}
          fields={{ ...((editing.permission.resource as { fields?: FieldAccessValues }).fields ?? {}) }}
          ceilings={editingDerived ? fieldCeilings(editing.permission, kinds) : undefined}
          readOnly={readOnly || editing.permission.isDisabled}
          onClose={() => setEditing(undefined)}
          onSave={readOnly ? undefined : (next) => changes.changeFields(editing.permission, kind, next)}
        />
      )}
    </Box>
  );
}

export default function PersonRecordList(props: PersonRecordListProps) {
  const { kind, count, person, open, onToggle } = props;
  const removed = props.mode === "removed";
  const listId = useId();
  const noun = kind.targetNoun ?? kind.name;
  const kindName = kindLabel(kind);

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.5, rowGap: 0.5, px: 2, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, flex: "1 1 10rem" }}>
          {kindName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {removed ? removedRecordCount(count, noun) : recordCount(count, noun)}
        </Typography>
        <Button
          size="small"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={open ? accessibleNames.hideRecords(noun, person.name) : accessibleNames.showRecords(noun, person.name)}
          onClick={onToggle}
          endIcon={<ChevronRightIcon sx={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />}
          sx={[{ minHeight: 40 }, touchTargetSx()]}
        >
          {open ? "Hide" : "Show"}
        </Button>
      </Box>
      {open && <RecordRows {...props} listId={listId} />}
    </Box>
  );
}
